import {useParams} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import firebaseState, {firebaseSessionIdState, firebaseUidState} from "../state/firebaseState";
import useUser from "./useUser";
import {useEffect} from "react";
import firebaseApp from "../services/firebase";
import queryClient from "../services/queryClient";
import firebase from "firebase/app";
import "firebase/database";

const useSession = () => {
  const { timing } = useParams();
  const setFirebase = useSetRecoilState(firebaseState);
  const firebaseSessionId = useRecoilValue(firebaseSessionIdState);
  const firebaseUid = useRecoilValue(firebaseUidState);
  const user = useUser();

  useEffect(() => {
    if(!firebaseUid) {
      return;
    }

    const sessionRef = firebaseApp.database()
    .ref('users')
    .child(firebaseUid)
    .child('sessions')
    .push();

    const onConnected = (snap) => {
      setFirebase(old => ({...old, sessionId: sessionRef.key, connected: snap.val()}));
      if (snap.val() !== true) {
        return;
      }

      // Update sessions for user
      sessionRef.onDisconnect().set(null);
      sessionRef.set(true);
    };

    const connectedRef = firebaseApp.database().ref(".info/connected");
    connectedRef.on("value", onConnected);

    const userRef = firebaseApp.database().ref(`users/${firebaseUid}`);

    const onUser = (snapshot) => setFirebase(old => ({...old, user: snapshot.val()}));
    userRef.on('value', onUser);

    return () => {
      connectedRef.off("value", onConnected);

      sessionRef.set(null);

      userRef.off('value', onUser);
    }
  }, [firebaseUid, timing]);

  useEffect(() => {
    if(!timing || !firebaseUid || !firebaseSessionId) {
      return;
    }

    const activeSessionRef = firebaseApp.database()
    .ref('room-sessions')
    .child(timing)
    .child(firebaseUid)
    .child(firebaseSessionId);

    activeSessionRef.child('loggedOut').onDisconnect().set(firebase.database.ServerValue.TIMESTAMP);
    activeSessionRef.child('active').onDisconnect().set(false);
    activeSessionRef.set({
      active: true,
      loggedIn: firebase.database.ServerValue.TIMESTAMP,
      userAgent: navigator?.userAgent,
      localDate: Date.now(),
    });

    return () => {
      activeSessionRef.child('loggedOut').set(firebase.database.ServerValue.TIMESTAMP);
      activeSessionRef.child('active').set(false);
    };
  }, [timing, firebaseUid, firebaseSessionId]);

  useEffect(() => {
    if(!firebaseUid || !user) {
      return;
    }

    firebaseApp.auth().currentUser.updateProfile({
      displayName: user.name,
      photoURL: user.avatar,
    });

    firebaseApp.database()
    .ref('users')
    .child(firebaseUid)
    .update({
      name: user.name,
      avatar: user?.avatar_thumb || null,
    });
  }, [firebaseUid,  user]);

  useEffect(() => {
    if(!firebaseUid || !firebaseSessionId || !user) {
      return;
    }

    const roomUsersRef = firebaseApp.database()
    .ref('room-users')
    .child(timing)
    .child(firebaseUid);

    const sessionRef = roomUsersRef
    .child(firebaseSessionId);

    const onChanged = () => {
      console.log('DBG invalidate');
      queryClient.invalidateQueries(['valid', timing])
    };

    sessionRef.onDisconnect().set(null);
    sessionRef
    .set({
      id: firebaseUid,
      name: user.name,
      avatar: user?.avatar_thumb || null,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
    }, () => {
      roomUsersRef.on('value', onChanged);
    });

    return () => {
      sessionRef.set(null);
      roomUsersRef.off('value', onChanged);
    };
  }, [timing, firebaseUid, firebaseSessionId, user]);
}

export default useSession;
